import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@ncoder/ui";
import { useSpring, useTrail, animated } from "@react-spring/web";
import { Bot, Loader2, Pencil, Plus, Sparkles } from "lucide-react";
import { AgentTypeBadge } from "@/components/organisms/studio/AgentTypeBadge";
import { CollapsibleSection } from "@/components/atoms/studio/CollapsibleSection";
import { SearchableSelect } from "@/components/organisms/studio/SearchableSelect";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { useCreateAgent, useUpdateAgent, usePlanAgent, useDropdown } from "@/hooks/studio-platform";
import type { AgentRecord } from "@/services/studio/types";

const COMMON_SF_METADATA_TYPES = [
  { value: "ApexClass", label: "ApexClass" },
  { value: "ApexTrigger", label: "ApexTrigger" },
  { value: "CustomObject", label: "CustomObject" },
  { value: "Flow", label: "Flow" },
  { value: "PermissionSet", label: "PermissionSet" },
  { value: "Profile", label: "Profile" },
  { value: "ValidationRule", label: "ValidationRule" },
  { value: "WorkflowRule", label: "WorkflowRule" },
  { value: "ApprovalProcess", label: "ApprovalProcess" },
  { value: "Report", label: "Report" },
  { value: "Dashboard", label: "Dashboard" },
  { value: "EmailTemplate", label: "EmailTemplate" },
  { value: "LightningComponentBundle", label: "LightningComponentBundle" },
  { value: "AuraDefinitionBundle", label: "AuraDefinitionBundle" },
  { value: "StaticResource", label: "StaticResource" },
];

interface CreateAgentPanelProps {
  open: boolean;
  onClose: () => void;
  onCreated: (agentId: string) => void;
  /** When set, the panel edits this agent instead of creating a new one. */
  agent?: AgentRecord | null;
  /** Called after a successful edit (distinct from onCreated so callers can re-run). */
  onUpdated?: (agentId: string) => void;
}

export function CreateAgentPanel({ open, onClose, onCreated, agent, onUpdated }: CreateAgentPanelProps) {
  const isEdit = Boolean(agent);
  // ── Form state ───────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [metadataType, setMetadataType] = useState("");
  const [input, setInput] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);

  const planner = usePlanAgent();
  const create = useCreateAgent();
  const update = useUpdateAgent();
  const pending = create.isPending || update.isPending;

  const metadataTypesQ = useDropdown("list_metadata_types", {}, true);
  const apiMetadataOptions = metadataTypesQ.data?.options?.filter((o) => o.value !== "__all__") ?? [];
  // When API returns nothing (org has no imported metadata or service unreachable)
  // fall back to the common Salesforce metadata types so the dropdown is always usable.
  const metadataTypeOptions = !metadataTypesQ.isLoading && apiMetadataOptions.length === 0
    ? COMMON_SF_METADATA_TYPES
    : apiMetadataOptions;

  // Reset/pre-fill form when panel opens. Edit mode seeds from the agent so the
  // user tweaks the existing definition; create mode starts blank.
  useEffect(() => {
    if (open) {
      if (agent) {
        setName(agent.name);
        setInput(agent.goal || agent.description || "");
        setMetadataType(typeof agent.scope?.metadata_type === "string" ? (agent.scope.metadata_type as string) : "");
      } else {
        setName(""); setMetadataType(""); setInput("");
      }
      planner.reset();
      const t = setTimeout(() => nameRef.current?.focus(), 300);
      return () => clearTimeout(t);
    }
  }, [open, agent]);

  // Auto-plan on debounce
  useEffect(() => {
    const t = setTimeout(() => {
      if (name.trim() && input.trim()) {
        planner.mutate({
          name,
          input,
          agent_type: isEdit && agent ? agent.agent_type : "queryable",
          ...(metadataType ? { scope: { metadata_type: metadataType } } : {}),
        });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [name, input, metadataType]);

  const planned = planner.data?.plan;
  const tools = planned?.tools ?? [];

  const submit = async () => {
    if (isEdit && agent) {
      // Edit: send only the editable surface. The backend drops the crystallized
      // recipe so the next run re-discovers tools for the changed definition.
      const result = await update.mutateAsync({
        agentId: agent.id,
        body: {
          name,
          input,
          scope: metadataType ? { metadata_type: metadataType } : {},
          // Keep the agent's current tools when the planner hasn't re-run.
          tools: planned?.tools ?? agent.tools,
          plan: planned?.plan ?? undefined,
          config: planned?.config,
          llm_config: planned?.llm_config,
        },
      });
      onUpdated?.(result.agent.id);
      return;
    }
    const result = await create.mutateAsync({
      name,
      input,
      agent_type: "queryable",
      scope: metadataType ? { metadata_type: metadataType } : undefined,
      tools: planned?.tools ?? [],
      plan: planned?.plan ?? [],
      config: planned?.config,
      llm_config: planned?.llm_config,
      capabilities: planned?.capabilities,
    });
    onCreated(result.agent.id);
  };

  // ── Springs (plan section + tool chips only — sheet animation is CSS) ────
  const planSpring = useSpring({
    opacity: planned ? 1 : 0,
    transform: planned ? "translateY(0px)" : "translateY(6px)",
    config: { tension: 320, friction: 28 },
  });

  // key={planned?.slug} on wrapper resets trail for each new plan
  const toolTrail = useTrail(tools.length, {
    from: { opacity: 0, transform: "scale(0.88)" },
    to: { opacity: 1, transform: "scale(1)" },
    config: { tension: 380, friction: 26 },
  });

  // Block submit while planning or when planning failed for new agents (no tools = broken agent).
  // Edit mode can still save using the agent's existing tools as fallback.
  const canSubmit = Boolean(
    name.trim() && input.trim() && !pending && !planner.isPending && (isEdit || !planner.isError),
  );

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:w-[480px] sm:max-w-none"
      >
        {/* Radix requires Title + Description for accessibility */}
        <SheetTitle className="sr-only">{isEdit ? "Edit Agent" : "Create Agent"}</SheetTitle>
        <SheetDescription className="sr-only">
          {isEdit
            ? "Edit this agent's name, scope, and instructions. Saving re-selects its tools on the next run."
            : "Create a new agent by providing a name, type, and job description."}
        </SheetDescription>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border bg-muted/20 px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            {isEdit ? <Pencil className="h-4 w-4 text-primary" /> : <Bot className="h-4 w-4 text-primary" />}
          </div>
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {isEdit ? "Edit Agent" : "New Agent"}
            </p>
            <h2 className="text-base font-bold leading-tight">{isEdit ? (agent?.name || "Edit Agent") : "Create Agent"}</h2>
          </div>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────────────── */}
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">

          {/* Required fields */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="font-mono text-xs text-muted-foreground">Agent name</label>
              <Input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Release Readiness Agent"
              />
            </div>

            <div className="space-y-1.5">
              <label className="font-mono text-xs text-muted-foreground">Metadata type</label>
              <SearchableSelect
                value={metadataType}
                onChange={setMetadataType}
                options={metadataTypeOptions}
                placeholder={metadataTypesQ.isLoading ? "Loading…" : "Select metadata type (optional)"}
                emptyHint="No matching metadata type found."
                disabled={metadataTypesQ.isLoading && metadataTypeOptions.length === 0}
              />
            </div>

            <div className="space-y-1.5">
              <label className="font-mono text-xs text-muted-foreground">
                What should this agent do?
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Check Apex coverage, dependency risk, deployment readiness, and create a weekly report."
                className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* ── Auto-plan inline preview ─────────────────────────────────── */}
          {(planner.isPending || planned) && (
            <animated.div style={planSpring}>
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/10">
                <div className="flex items-center justify-between border-b border-border/50 bg-muted/20 px-4 py-2.5">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Auto Plan
                  </span>
                  {planner.isPending && (
                    <div className="flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      <span className="font-mono text-[10px] text-muted-foreground">Generating…</span>
                    </div>
                  )}
                  {planner.isError && planned && !planner.isPending && (
                    <span className="font-mono text-[10px] text-amber-500">Re-plan failed — showing previous plan</span>
                  )}
                  {planned && !planner.isError && !planner.isPending && (
                    <div className="flex items-center gap-1.5">
                      <AgentTypeBadge type={planned.agent_type} />
                      <Badge variant="outline">{planned.safety_level}</Badge>
                    </div>
                  )}
                </div>

                {planned && (
                  <div className="space-y-4 p-4">
                    <p className="text-sm text-muted-foreground">{planned.description}</p>

                    {/* Tool chips with stagger trail */}
                    <div>
                      <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {tools.length} tools auto-selected
                      </p>
                      <div key={planned.slug} className="flex flex-wrap gap-1.5">
                        {toolTrail.map((style, idx) => (
                          <animated.span
                            key={tools[idx]}
                            style={style}
                            className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-background px-2 py-1 font-mono text-[11px]"
                          >
                            <Sparkles className="h-2.5 w-2.5 shrink-0 text-primary" />
                            {tools[idx]}
                          </animated.span>
                        ))}
                      </div>
                    </div>

                    {/* Plan steps */}
                    {planned.plan.length > 0 && (
                      <CollapsibleSection
                        title={`${planned.plan.length} execution steps`}
                        defaultOpen={false}
                      >
                        <div className="space-y-1.5 p-3">
                          {planned.plan.map((step, idx) => (
                            <div
                              key={step.id}
                              className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 rounded-md bg-muted/20 px-3 py-2"
                            >
                              <span className="pt-0.5 font-mono text-[10px] text-muted-foreground">
                                {idx + 1}.
                              </span>
                              <div>
                                <p className="font-mono text-xs font-semibold">{step.title}</p>
                                <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}
                  </div>
                )}
              </div>
            </animated.div>
          )}

          {planner.isError && !isEdit && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              Auto-planning failed — adjust your description to retry, or try again in a moment.
            </p>
          )}

          {(create.error || update.error) && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {(create.error || update.error)?.message ?? "Something went wrong — please try again."}
            </p>
          )}

          {isEdit && (
            <p className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
              Saving updates this agent and <strong className="text-foreground">re-selects its tools on the next run</strong> — the saved recipe is cleared so it re-discovers against your changes.
            </p>
          )}
        </div>

        {/* ── Sticky footer ────────────────────────────────────────────────── */}
        <div className="shrink-0 border-t border-border bg-muted/10 px-6 py-4">
          <Button
            type="button"
            variant="charcoal"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full"
          >
            {(pending || planner.isPending)
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : isEdit ? <Pencil className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {planner.isPending
              ? "Planning…"
              : isEdit ? "Save & re-run" : "Create Agent"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
